import React, { useEffect } from 'react';
import notificationService from './NotificationService';
import { Task } from '../types/task';
import { useTranslation } from 'react-i18next';
import { formatInTimeZone } from 'date-fns-tz';
import { parseISO } from 'date-fns';
import { useAtom } from 'jotai';
import { tasksAtom } from '../atoms/v0/taskAtoms';
import { logger } from '../lib/logger';

/**
 * 计算任务提醒的触发时间
 */
export const calculateReminderTimes = (task: Task): Date[] => {
  const reminderTimes: Date[] = [];

  if (!task.reminders || task.reminders.length === 0) return reminderTimes;

  // 处理每个提醒设置
  task.reminders.forEach(reminder => {
    const trigger = reminder.trigger;

    if (trigger.type === 'ABSOLUTE' && trigger.absoluteTime) {
      // 绝对时间提醒
      try {
        const date = parseISO(trigger.absoluteTime);
        if (!isNaN(date.getTime())) {
          reminderTimes.push(date);
        }
      } catch (e) {
        console.error('Invalid absolute time in reminder:', trigger.absoluteTime);
      }
    } else if (trigger.type === 'RELATIVE' && trigger.offsetSeconds !== undefined) {
      // 相对时间提醒
      let baseTime: Date | undefined;

      // 确定基准时间
      if (trigger.related === 'START' && task.startAt) {
        baseTime = parseISO(task.startAt);
      } else if (trigger.related === 'CALCULATED_END' && task.startAt && task.cost) {
        const start = parseISO(task.startAt);
        baseTime = new Date(start.getTime() + task.cost * 1000);
      } else if (trigger.related === 'DUE' && task.dueAt) {
        baseTime = parseISO(task.dueAt);
      }

      // 计算提醒时间
      if (baseTime && !isNaN(baseTime.getTime())) {
        const reminderTime = new Date(baseTime.getTime() + trigger.offsetSeconds * 1000);
        reminderTimes.push(reminderTime);
      }
    }
  });

  return reminderTimes;
};

/**
 * 获取任务通知配置
 */
export const getTaskNotificationOptions = (task: Task, t: Function) => {
  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  return {
    id: `task-${task.id}`,
    title: t('v0/tasks:reminder.title', '任务提醒'),
    body: `${task.title}: ${task.notes || t('v0/tasks:reminder.noDescription', '没有描述')}`,
    icon: '/icon.png',
    sound: true,
    category: 'reminder',
    priority: 1,
    extra: {
      taskId: task.id,
      type: 'task_reminder',
      url: `/tasks/${task.id}`
    }
  };
};

/**
 * 调度任务提醒通知
 */
export const scheduleTaskReminders = async (tasks: Task[], t: Function) => {
  // 直接使用导入的实例
  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // 先取消所有已存在的任务提醒
  try {
    const pendingNotifications = await notificationService.getPendingNotifications();
    console.log("获取到待处理通知:", pendingNotifications);

    // 处理不同的返回格式，确保我们有一个通知数组
    let taskNotifications: any[] = [];

    if (Array.isArray(pendingNotifications)) {
      // 如果是数组，直接过滤
      taskNotifications = pendingNotifications.filter(
        (n: any) => n.extra && typeof n.extra === 'object' && n.extra.type === 'task_reminder'
      );
    } else if (pendingNotifications && typeof pendingNotifications === 'object') {
      // 如果是对象，检查是否有 notifications 属性
      // 使用类型断言避免类型错误
      const notificationsObject = pendingNotifications as any;
      const notificationsArray = notificationsObject.notifications || [];
      if (Array.isArray(notificationsArray)) {
        taskNotifications = notificationsArray.filter(
          (n: any) => n.extra && typeof n.extra === 'object' && n.extra.type === 'task_reminder'
        );
      }
    }

    console.log("找到任务相关通知:", taskNotifications.length);

    // 逐个取消任务通知
    for (const notification of taskNotifications) {
      if (notification.id) {
        await notificationService.cancelLocalNotification(notification.id.toString());
        console.log(`取消通知: ${notification.id}`);
      }
    }
  } catch (error) {
    console.error("获取或取消通知时出错:", error);
  }

  // 为有提醒的任务调度新通知
  for (const task of tasks) {
    const reminderTimes = calculateReminderTimes(task);

    for (const [index, reminderTime] of reminderTimes.entries()) {
      // 只处理未来的提醒
      if (reminderTime.getTime() > Date.now()) {
        const options = getTaskNotificationOptions(task, t);

        // 如果一个任务有多个提醒，添加索引以区分
        if (reminderTimes.length > 1) {
          options.id = `task-${task.id}-${index}`;
          // 添加提醒时间到通知内容
          const formattedTime = formatInTimeZone(
            reminderTime,
            userTimeZone,
            'yyyy-MM-dd HH:mm'
          );
          options.body += `\n${t('v0/tasks:reminder.time', '时间')}: ${formattedTime}`;
        }

        // 准备通知选项，适配 Capacitor 格式
        const notificationOptions = {
          ...options,
          id: options.id, // 保持 id 为字符串格式
          schedule: { at: reminderTime },
          // 确保需要的属性存在
          title: options.title,
          body: options.body,
          extra: options.extra
        };

        try {
          await notificationService.scheduleLocalNotification(notificationOptions);
          console.log(`Scheduled reminder for task "${task.title}" at ${reminderTime}`);
        } catch (error) {
          console.error(`Failed to schedule reminder for task "${task.title}":`, error);
        }
      }
    }
  }
};

/**
 * 使用此 Hook 在组件中处理任务提醒
 */
export const useTaskReminders = () => {
  const { t } = useTranslation(['v0/tasks', 'v0/common']);
  const [tasks] = useAtom(tasksAtom);

  // 在任务列表变化时重新调度提醒 - 使用 useRef 跟踪上一次的任务数量，避免不必要的重新调度
  const previousTaskCountRef = React.useRef<number>(0);

  useEffect(() => {
    // 只有当任务数量变化时才重新调度，避免不必要的调用
    if (tasks.length !== previousTaskCountRef.current) {
      console.log("任务列表数量已变化，重新调度提醒。任务数量:", tasks.length);

      // 更新引用值
      previousTaskCountRef.current = tasks.length;

      // 使用 Promise 处理异步操作
      const schedulePromise = async () => {
        try {
          await scheduleTaskReminders(tasks, t);
          console.log("任务提醒调度完成");
        } catch (error) {
          console.error("任务提醒调度失败:", error);
        }
      };

      // 执行调度
      schedulePromise();
    }

    // 组件卸载时不需要清理，因为通知应该保持
  }, [tasks.length, t]); // 只依赖任务数量，而不是整个任务数组
};